import { NgModule } from '@angular/core';
import { RouterModule, Routes, TitleStrategy } from '@angular/router';
import { MsalGuard, MsalRedirectComponent } from '@azure/msal-angular';
import { BrowserUtils } from '@azure/msal-browser';
import { CanActivateGuard } from './core/guards/can-activate.guard';
import { effectivePermissionsGuard } from './features/guards/effective-permissions.guard';
import { LandingRedirectComponent } from './features/components/rpe/landing-redirect/landing-redirect.component';
import { breadcrumbFromDashboardGuard } from './core/guards/breadcrumb-from-dashboard.guard';
import { SummaryPagePrintComponent } from './shared/components/summary-page-print/summary-page-print.component';
import { InformationPagePrintComponent } from './shared/components/information-page-print/information-page-print.component';
import { ShipmentLabelGenerationComponent } from './shared/components/shipment-label-generation/shipment-label-generation.component';
import { NestedPrintPreviewComponent } from './shared/components/nested-print-preview/nested-print-preview.component';
import { PageTitleStrategy } from './shared/services/page-title.strategy';

const routes: Routes = [
  {
    path: 'shortcut-keys',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard],
    loadChildren: () =>
      import('./features/shortcut-keys/shortcut-keys.module').then(
        m => m.ShortcutKeysModule
      ),
    data: { breadcrumb: { alias: 'shortcut-keys' } },
  },
  {
    path: 'home',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard],
    loadChildren: () =>
      import('./features/components/home/home.module').then((m) => m.HomeModule),
    data: { breadcrumb: { alias: 'home' } },
  },
  {
    path: 'dashboard',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard, effectivePermissionsGuard],
    loadChildren: () =>
      import('./features/components/dashboard/dashboard.module').then(m => m.DashboardModule),
  },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [CanActivateGuard],
    component: LandingRedirectComponent,
  },
  {
    path: 'hawb',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard, effectivePermissionsGuard],
    loadChildren: () =>
      import('./features/components/hawb/hawb.module').then(m => m.HawbModule),
    data: { breadcrumb: { alias: 'hawb' } },
  },
  {
    path: 'bag',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard, effectivePermissionsGuard],
    loadChildren: () =>
      import('./features/components/bag/bag.module').then(m => m.BagModule),
    data: { breadcrumb: { alias: 'bag' } },
  },
  {
    path: 'manifest',
    canActivate: [breadcrumbFromDashboardGuard, MsalGuard, effectivePermissionsGuard],
    loadChildren: () =>
      import('./features/components/manifest/manifest.module').then(
        m => m.ManifestModule
      ),
    data: { breadcrumb: { alias: 'manifest' } },
  },
  {
    path: 'cancel-recover-shipments',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard],
    loadChildren: () =>
      import(
        './features/components/cancel-recover-shipments/cancel-recover-shipments.module'
      ).then(m => m.CancelRecoverShipmentsModule),
    data: { breadcrumb: { alias: 'cancel-recover-shipments' } },
  },
  {
    path: 'mawb',
    canActivate: [breadcrumbFromDashboardGuard, MsalGuard, effectivePermissionsGuard],
    loadChildren: () =>
      import('./features/components/mawb/mawb.module').then(m => m.MawbModule),
    data: { breadcrumb: { alias: 'mawb' } },
  },
  {
    path: 'sm-dashboard',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard, effectivePermissionsGuard],
    loadChildren: () =>
      import('./features/components/smDashboard/sm-dashboard.module').then(
        m => m.SmDashboardModule
      ),
    data: { breadcrumb: { alias: 'sm-dashboard' } },
  },
  {
    path: 'print-preview',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard],
    component: SummaryPagePrintComponent,
    data: { hideLayout: true },
  },
  {
    path: 'info-print-preview',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard],
    component: InformationPagePrintComponent,
    data: { hideLayout: true },
  },
  {
    path: 'nested-print-preview',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard],
    component: NestedPrintPreviewComponent,
    data: { hideLayout: true },
  },
  {
    path: 'shipment-label-generation-preview',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard],
    component: ShipmentLabelGenerationComponent,
    data: { hideLayout: true },
  },
  // {
  //   // Needed for handling redirect after login
  //   path: 'auth',
  //   component: MsalRedirectComponent,
  // },
  {
    path: 'user-profile',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard],
    loadChildren: () =>
      import(
        '../app/features/components/user-profile/user-profile.module'
      ).then(module => module.UserProfileModule),
    data: { breadcrumb: { alias: 'user-profile' } },
  },
  {
    path: 'accounts-payable',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard, effectivePermissionsGuard],
    loadChildren: () =>
      import('./features/components/accounts-payable/accounts-payable.module').then(
        m => m.AccountPayableModule
      ),
    data: { breadcrumb: { alias: 'accounts-payable' } },
  },
  {
    path: 'accounts-receivable',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard, effectivePermissionsGuard],
    loadChildren: () =>
      import('./features/components/accounts-receivable/accounts.receivable.module').then(
        m => m.AccountReceivableModule
      ),
    data: { breadcrumb: { alias: 'accounts-receivable' } },
  },
  {
    path: 'reports',
    canActivate: [breadcrumbFromDashboardGuard, CanActivateGuard, effectivePermissionsGuard],
    loadChildren: () =>
      import('./features/components/reports/reports.module').then(
        m => m.ReportsModule
      ),
    data: { breadcrumb: { alias: 'reports' } },
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      canceledNavigationResolution: 'computed',
      bindToComponentInputs: true,
      // Don't perform initial navigation in iframes or popups
      initialNavigation:
        !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
          ? 'enabledNonBlocking'
          : 'disabled', // Set to enabledBlocking to use Angular Universal
    }),
  ],
  exports: [RouterModule],
  providers: [{ provide: TitleStrategy, useClass: PageTitleStrategy }],
})
export class AppRoutingModule {}
